import { RESERVED_ROOT_PATHS } from "./routePaths";

export const ROUTE_PATTERNS = [
  "/",
  "/bio",
  "/songs",
  "/songs/:trackName",
  "/albums",
  "/albums/:albumName",
  "/videos",
  "/merch",
  "/merch/thanks",
  "/merch/orders/:token",
  "/merch/:designSlug",
  "/games",
  "/games/endless",
  "/games/:songSlug",
  "/games/:songSlug/:gameKind",
  "/newsletter/confirm",
  "/newsletter/unsubscribe",
  "/admin/*",
  "/branches/*",
  "/members/*",
  "/redirect/:platform/:kind/:id",
  "/redirect/:kind/:id",
  "/r/:platform/:kind/:id",
  "/r/:kind/:id",
  "/:artist",
  "/:artist/bio",
  "/:artist/songs",
  "/:artist/songs/:trackName",
  "/:artist/albums",
  "/:artist/albums/:albumName",
  "/:artist/videos",
  "/:artist/:release",
] as const;

export const UNKNOWN_ROUTE_PATTERN = "/*";

type CompiledPattern = { pattern: string; segments: string[]; wildcard: boolean };

const compiled: CompiledPattern[] = ROUTE_PATTERNS.map((pattern) => {
  const segments = pattern.split("/").filter(Boolean);
  const wildcard = segments[segments.length - 1] === "*";
  return { pattern, segments: wildcard ? segments.slice(0, -1) : segments, wildcard };
});

const staticScore = (entry: CompiledPattern) =>
  entry.segments.reduce((score, segment) => score + (segment.startsWith(":") ? 0 : 1), 0);

const ranked = [...compiled].sort((a, b) => {
  if (staticScore(a) !== staticScore(b)) return staticScore(b) - staticScore(a);
  if (a.segments.length !== b.segments.length) return b.segments.length - a.segments.length;
  return Number(a.wildcard) - Number(b.wildcard);
});

export function splitPathAndQuery(value: string | null | undefined): { path: string; search: string } {
  const raw = (value || "/").trim();
  const hashIndex = raw.indexOf("#");
  const withoutHash = hashIndex >= 0 ? raw.slice(0, hashIndex) : raw;
  const queryIndex = withoutHash.indexOf("?");
  const path = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const search = queryIndex >= 0 ? withoutHash.slice(queryIndex) : "";
  return { path: path || "/", search: search === "?" ? "" : search };
}

export function normalizePathname(value: string | null | undefined): string {
  const { path } = splitPathAndQuery(value);
  const withLeadingSlash = path.startsWith("/") ? path : `/${path}`;
  if (withLeadingSlash.length > 1 && withLeadingSlash.endsWith("/")) {
    return withLeadingSlash.replace(/\/+$/, "") || "/";
  }
  return withLeadingSlash;
}

const isReservedRoot = (value: string): boolean =>
  RESERVED_ROOT_PATHS.includes(value.toLowerCase() as (typeof RESERVED_ROOT_PATHS)[number]);

export function routePatternFor(value: string | null | undefined): string {
  const pathname = normalizePathname(value);
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return "/";

  const root = parts[0].toLowerCase();
  const reserved = isReservedRoot(root);
  const candidates = reserved ? ranked.filter((entry) => entry.segments[0]?.toLowerCase() === root) : ranked;

  for (const entry of candidates) {
    if (entry.segments.length === 0) continue;
    if (entry.wildcard ? parts.length < entry.segments.length : parts.length !== entry.segments.length) continue;
    const matches = entry.segments.every((segment, index) =>
      segment.startsWith(":") ? Boolean(parts[index]) : segment.toLowerCase() === parts[index].toLowerCase(),
    );
    if (matches) return entry.pattern;
  }

  if (reserved) return `/${root}/*`;
  return UNKNOWN_ROUTE_PATTERN;
}

export function canonicalPagePath(value: string | null | undefined): string {
  return normalizePathname(value);
}

export function fullPagePath(pathname: string | null | undefined, search?: string | null): string {
  const base = normalizePathname(pathname);
  const raw = (search || "").trim();
  if (!raw || raw === "?") return base;
  return `${base}${raw.startsWith("?") ? raw : `?${raw}`}`;
}
